import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Check,
  ShieldCheck,
  Loader2,
  Coins,
  Zap,
  Crown,
  Star,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MarketingNav, MarketingFooter } from "@/components/TailorWizMarketingHome";
import { openPayPal } from "@/lib/paypal";

interface SubscriptionPlan {
  id: string;
  name: string;
  monthly_credits: number;
  price: number;
  features: string[];
  is_free: boolean;
}

// Fallback — the real plans (used if the subscription_plans table is unavailable)
const FALLBACK_PLANS: SubscriptionPlan[] = [
  { id: "starter", name: "Starter", monthly_credits: 50, price: 19, is_free: false, features: ["50 credits per month", "Resume tailoring", "Cover letters", "Match score reports", "Priority support"] },
  { id: "pro", name: "Pro", monthly_credits: 150, price: 49, is_free: false, features: ["150 credits per month", "All Starter features", "LinkedIn optimization", "Executive bios", "Bulk generation", "Priority support"] },
  { id: "executive", name: "Executive", monthly_credits: 500, price: 199, is_free: false, features: ["500 credits per month", "All Pro features", "White-label exports", "API access", "Dedicated support"] },
  { id: "enterprise", name: "Enterprise", monthly_credits: 9999, price: 495, is_free: false, features: ["Custom credit allocation", "Dedicated account manager", "Custom integrations", "Team management", "SLA support", "Volume discounts"] },
];

function planIcon(name: string) {
  switch (name.toLowerCase()) {
    case "free": return Coins;
    case "starter": return Zap;
    case "pro": return Crown;
    case "executive": return Sparkles;
    case "enterprise": return Star;
    default: return Coins;
  }
}

const FAQ = [
  { q: "What are credits?", a: "Credits power every action — tailoring resumes, generating cover letters, running match scores, and more. Different actions cost different amounts of credits." },
  { q: "Do credits expire?", a: "Monthly subscription credits refresh each billing cycle. Any unused credits roll over for one month." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel your subscription at any time — you keep your credits until the end of the billing period. No contracts, no hidden fees." },
  { q: "What payment methods do you accept?", a: "All major credit and debit cards through our secure checkout. After payment, a real person activates your membership — usually the same day." },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { balance, plan: currentPlan, refetch: refetchCredits } = useCredits();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isProcessingPayment = false;
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("is_active", true)
        .order("price_cents", { ascending: true });
      if (error) throw error;
      const parsed: SubscriptionPlan[] = (data || [])
        .filter((p: any) => p.name !== "Test") // hidden internal 25-cent test plan
        .map((p: any) => ({
        id: p.id,
        name: p.name,
        monthly_credits: p.monthly_credits,
        price: Math.round((p.price_cents || 0) / 100),
        features: Array.isArray(p.features) ? (p.features as string[]) : [],
        is_free: p.is_free,
      }));
      setPlans(parsed.length ? parsed : FALLBACK_PLANS);
    } catch (e) {
      console.error("Error loading plans:", e);
      setPlans(FALLBACK_PLANS);
    } finally {
      setIsLoading(false);
    }
  };

  // Sales-led: plan buttons open the PayPal checkout for that plan (pay first,
  // then create the account). Activation is manual, usually same day.
  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (plan.is_free) {
      navigate(user ? "/" : `/signup?plan=${plan.id}`);
      return;
    }
    if (!user) {
      navigate(`/signup?plan=${plan.id}`);
      return;
    }
    setCheckoutLoading(plan.id);
    try {
      const { data, error } = await supabase.functions.invoke("square-checkout", {
        body: { plan_id: plan.id },
      });
      if (error || !data?.success || !data?.checkout_url) {
        throw new Error(data?.error || error?.message || "Couldn't start checkout.");
      }
      window.location.href = data.checkout_url;
    } catch (e: any) {
      toast.error(e.message || "Couldn't start checkout — try again.");
      setCheckoutLoading(null);
    }
  };

  const isCurrentPlan = (plan: SubscriptionPlan) =>
    currentPlan?.name?.toLowerCase() === plan.name.toLowerCase();

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.16]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 25%, #C9A84C 0, transparent 38%), radial-gradient(circle at 82% 75%, #4A78C2 0, transparent 38%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-14 sm:pt-20 sm:pb-16 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] text-[#C9A84C] bg-white/5 px-4 py-2 rounded-full border border-[#C9A84C]/40">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            Simple, Transparent Pricing
          </span>
          <h1 className="mt-6 text-[34px] sm:text-[48px] font-extrabold tracking-tight leading-[1.07]">
            {user ? "Upgrade Your Plan" : "Choose Your Plan"}
          </h1>
          <p className="mt-5 text-[16px] sm:text-[18px] leading-[1.6] text-slate-200">
            {user
              ? "Get more credits and unlock premium features to supercharge your job search."
              : "Simple monthly memberships. Credits power every tool. No hidden fees, cancel anytime."}
          </p>
          {user && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/5 border border-[#C9A84C]/40 px-4 py-1.5 text-[13px]">
              <Coins className="w-4 h-4 text-[#C9A84C]" />
              <span className="font-bold text-[#C9A84C]">{balance}</span>
              <span className="text-slate-300">credits remaining</span>
            </div>
          )}
        </div>
      </section>

      {/* FREE TRIAL BANNER */}
      <div className="bg-[#C9A84C]">
        <div className="max-w-5xl mx-auto px-6 py-3.5 text-center">
          <p className="text-[14px] sm:text-[15px] font-bold text-[#0B1C3F]">
            Start free with 10 credits every month — upgrade anytime from $19/month.
          </p>
        </div>
      </div>

      {/* PLANS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          {isLoading || isProcessingPayment ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-[#C9A84C]" />
              {isProcessingPayment && (
                <p className="text-slate-600 text-[15px]">Activating your subscription…</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 items-start">
              {plans.map((plan) => {
                const Icon = planIcon(plan.name);
                const isEnterprise = plan.name.toLowerCase() === "enterprise";
                const isPopular = plan.name.toLowerCase() === "pro";
                const isCurrent = isCurrentPlan(plan);
                const loading = checkoutLoading === plan.id;
                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl bg-white p-6 flex flex-col ${
                      isPopular
                        ? "border-2 border-[#C9A84C] shadow-xl xl:-mt-3 xl:mb-3"
                        : "border border-slate-200 shadow-sm"
                    }`}
                  >
                    {isPopular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-[#0B1C3F] text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                        Most Popular
                      </span>
                    )}
                    {isCurrent && (
                      <span className="absolute -top-3 right-3 bg-[#0B1C3F] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                        Current
                      </span>
                    )}
                    <div className="w-12 h-12 rounded-xl bg-[#0B1C3F] flex items-center justify-center">
                      <Icon className="w-5.5 h-5.5 text-[#C9A84C]" />
                    </div>
                    <h3 className="mt-3 text-[18px] font-extrabold text-[#0B1C3F]">{plan.name}</h3>
                    <p className="text-[12px] text-slate-500 font-medium">
                      {plan.monthly_credits >= 9999 ? "Unlimited" : plan.monthly_credits} credits/month
                    </p>
                    <div className="mt-3 pb-4 border-b border-slate-100">
                      <div className="flex items-baseline gap-1">
                        <span className="text-[38px] font-extrabold text-slate-900 tracking-tight">
                          {plan.is_free ? "Free" : `$${plan.price}`}
                        </span>
                        {!plan.is_free && <span className="text-[14px] text-slate-500 font-medium">/mo</span>}
                      </div>
                      {plan.is_free && (
                        <p className="text-[12px] text-emerald-600 font-semibold">10 credits every month — no card needed</p>
                      )}
                    </div>
                    <ul className="mt-4 space-y-2 flex-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                          <Check className="w-4 h-4 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => handleSelectPlan(plan)}
                      disabled={loading || isCurrent}
                      className={`mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-[14px] font-bold transition-all disabled:opacity-60 ${
                        isPopular
                          ? "bg-[#C9A84C] text-[#0B1C3F] hover:bg-[#E5C36A]"
                          : "bg-[#0B1C3F] text-white hover:bg-[#1a2d56]"
                      }`}
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Opening checkout…</>
                      ) : isCurrent ? (
                        "Current Plan"
                      ) : plan.is_free ? (
                        <>Start Free <ArrowRight className="w-4 h-4" /></>
                      ) : (
                        <>Subscribe Now <ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-2xl border-2 border-[#C9A84C]/40 bg-gradient-to-br from-[#0B1C3F] to-[#0E2350] text-white p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#C9A84C] flex items-center justify-center mx-auto">
              <ShieldCheck className="w-7 h-7 text-[#0B1C3F]" />
            </div>
            <h2 className="mt-4 text-[24px] sm:text-[30px] font-extrabold tracking-tight">
              The 90% Match Score Guarantee
            </h2>
            <p className="mt-3 text-[15px] sm:text-[16px] leading-[1.6] text-slate-200">
              Every document TailorWiz produces hits a 90% match score or better
              against the job you tailor it to. If a tailored document ever
              scores below 90%, the run is on us — our team rebuilds it
              personally at no charge.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-slate-300">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#C9A84C]" /> Secure checkout</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#C9A84C]" /> Cancel anytime</span>
              <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-[#C9A84C]" /> 30-day money back</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-center text-[26px] sm:text-[34px] font-extrabold tracking-tight text-slate-900">
            Pricing Questions
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-xl bg-white border border-slate-200 p-5">
                <h3 className="text-[15px] font-bold text-slate-900">{item.q}</h3>
                <p className="mt-1.5 text-[13px] leading-[1.55] text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-b from-[#0B1C3F] to-[#0E2350] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-[30px] sm:text-[40px] font-extrabold tracking-tight">
            Ready to land <span className="text-[#C9A84C]">more interviews?</span>
          </h2>
          <div className="mt-7">
            <button
              type="button"
              onClick={() => (user ? navigate("/") : window.scrollTo({ top: 0, behavior: "smooth" }))}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] transition-all"
            >
              {user ? "Go to Dashboard" : "Choose Your Plan"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
